import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import {
  SalesAdminCatalogPayload,
  SalesAdminManagedPriceListPayload,
  SalesAdminPriceRulePayload,
  normalizeSalesAdminPriceRuleLevel,
  salesAdminPriceRuleSkus,
} from '../../presidio-sales-admin/models/presidio-sales-admin.model';
import {
  createSalesAdminManagedPriceList,
  deleteSalesAdminCatalog,
  deleteSalesAdminPriceRule,
  listSalesAdminCatalogs,
  listSalesAdminManagedPriceLists,
  listSalesAdminPriceRules,
  newSalesAdminId,
  upsertSalesAdminCatalog,
  upsertSalesAdminPriceRule,
  withSalesAdminRequestCustomer,
} from './presidio-mock-sales-admin-state';

/** DEMO-ONLY HTTP handlers for Sales Admin REST under customers/{c}/sales-admin/* */
const ROOT = /^customers\/([^/]+)\/sales-admin(?:\/(.*))?$/i;

function ok<T>(req: HttpRequest<unknown>, body: T, status = 200): Observable<HttpEvent<unknown>> {
  return of(new HttpResponse({ status, body, url: req.url }));
}

/** Returns a normalized payload or a specific rejection reason for upload error messages. */
function normalizePricePayloadDetailed(
  raw: SalesAdminPriceRulePayload
): { payload: SalesAdminPriceRulePayload } | { error: string } {
  if (!raw?.level) {
    return { error: 'level is required' };
  }
  if ((raw as { level?: string }).level === 'category') {
    return {
      error: 'level "category" is not supported; use sku_flat, sku_percent, manufacturer, or catalog',
    };
  }

  const level = normalizeSalesAdminPriceRuleLevel(raw.level, raw.overrideType);
  if (!level) {
    return { error: `invalid level "${raw.level}"` };
  }

  const overrideType = level === 'sku_flat' ? 'flat' : 'percent';
  const skus =
    level === 'sku_flat' || level === 'sku_percent' ? salesAdminPriceRuleSkus(raw) : [];

  if ((level === 'sku_flat' || level === 'sku_percent') && !skus.length) {
    return { error: `at least one sku is required when level is ${level}` };
  }
  if (level === 'sku_flat' && skus.length > 1) {
    return { error: 'sku_flat allows only one product per rule' };
  }
  if (level === 'manufacturer' && !raw.manufacturer) {
    return { error: 'manufacturer is required when level is manufacturer' };
  }
  if (level === 'catalog' && !raw.catalogId) {
    return { error: 'catalog_id is required when level is catalog' };
  }
  if (level === 'catalog') {
    const exists = listSalesAdminCatalogs().some(c => c.id === raw.catalogId);
    if (!exists) {
      return { error: `custom catalog ID "${raw.catalogId}" does not exist` };
    }
  }
  if (level === 'sku_flat' && (raw.flatPrice == null || !Number.isFinite(Number(raw.flatPrice)))) {
    return { error: 'flat_price is required and must be a number when level is sku_flat' };
  }
  if (level !== 'sku_flat') {
    const pct = Number(raw.percentOff);
    if (raw.percentOff == null || !Number.isFinite(pct) || !Number.isInteger(pct) || pct < 0 || pct > 100) {
      return { error: 'percent_off must be a whole number from 0 to 100' };
    }
  }
  if (level !== 'sku_flat' && raw.flatPrice != null && Number.isFinite(Number(raw.flatPrice))) {
    return { error: `flat_price is not allowed for ${level}-level rules` };
  }

  const derivedName =
    (raw.name || '').trim() ||
    (level === 'sku_flat'
      ? `${skus[0]} flat`
      : level === 'sku_percent'
        ? `${skus.length === 1 ? skus[0] : `${skus.length} SKUs`} ${Math.trunc(Number(raw.percentOff))}%`
        : level === 'manufacturer'
          ? `${raw.manufacturer} ${Math.trunc(Number(raw.percentOff))}%`
          : `${raw.catalogId} ${Math.trunc(Number(raw.percentOff))}%`);

  return {
    payload: {
      name: derivedName,
      level,
      overrideType,
      priceListId: raw.priceListId,
      sku: skus[0],
      skus: skus.length ? skus : undefined,
      categoryId: raw.categoryId,
      manufacturer: level === 'manufacturer' ? raw.manufacturer : undefined,
      catalogId: level === 'catalog' ? raw.catalogId : undefined,
      flatPrice: level === 'sku_flat' ? Number(raw.flatPrice) : undefined,
      percentOff: level !== 'sku_flat' ? Math.trunc(Number(raw.percentOff)) : undefined,
      active: raw.active !== false,
    },
  };
}

function ruleFromPayload(payload: SalesAdminPriceRulePayload, id: string): Parameters<typeof upsertSalesAdminPriceRule>[0] {
  return {
    id,
    customerNo: '',
    priceListId: payload.priceListId || '',
    name: payload.name || id,
    level: payload.level,
    overrideType: payload.overrideType,
    sku: payload.sku,
    skus: payload.skus,
    categoryId: payload.categoryId,
    manufacturer: payload.manufacturer,
    catalogId: payload.catalogId,
    flatPrice: payload.flatPrice,
    percentOff: payload.percentOff,
    active: payload.active !== false,
    updatedAt: '',
    updatedBy: '',
  };
}

function normalizePricePayload(raw: SalesAdminPriceRulePayload): SalesAdminPriceRulePayload | null {
  const result = normalizePricePayloadDetailed(raw);
  return 'payload' in result ? result.payload : null;
}

export function handlePresidioMockSalesAdmin(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: <T>(body: unknown) => T
): Observable<HttpEvent<unknown>> | null {
  const match = ROOT.exec(restPath);
  if (!match) {
    return null;
  }

  const customerNo = decodeURIComponent(match[1] || '');
  const sub = (match[2] || '').replace(/\/$/, '');
  const method = req.method.toUpperCase();

  return withSalesAdminRequestCustomer(customerNo, () =>
    dispatchPresidioMockSalesAdmin(req, customerNo, sub, method, readJsonBody)
  );
}

function dispatchPresidioMockSalesAdmin(
  req: HttpRequest<unknown>,
  _customerNo: string,
  sub: string,
  method: string,
  readJsonBody: <T>(body: unknown) => T
): Observable<HttpEvent<unknown>> | null {
  // managed named price lists
  if (sub === 'managed-price-lists' && method === 'GET') {
    return ok(req, { elements: listSalesAdminManagedPriceLists() });
  }
  if (sub === 'managed-price-lists' && method === 'POST') {
    const body = readJsonBody<SalesAdminManagedPriceListPayload>(req.body);
    if (!body?.name?.trim()) {
      return ok(req, { error: 'name_required' }, 400);
    }
    return ok(req, createSalesAdminManagedPriceList(body.name), 201);
  }

  // price-lists (rules)
  if (sub === 'price-lists' && method === 'GET') {
    const listId =
      req.params.get('priceListId') ||
      new URL(req.url, 'http://local').searchParams.get('priceListId') ||
      undefined;
    return ok(req, { elements: listSalesAdminPriceRules(listId || undefined) });
  }
  if (sub === 'price-lists' && method === 'POST') {
    const payload = normalizePricePayload(readJsonBody(req.body));
    if (!payload) {
      return ok(req, { error: 'invalid_price_rule' }, 400);
    }
    const created = upsertSalesAdminPriceRule(ruleFromPayload(payload, newSalesAdminId('PR')), true);
    return ok(req, created, 201);
  }
  if (sub === 'price-lists/upload' && method === 'POST') {
    const body = readJsonBody<{ rows?: SalesAdminPriceRulePayload[]; priceListId?: string }>(req.body);
    let created = 0;
    const errors: string[] = [];
    (body?.rows || []).forEach((row, i) => {
      const result = normalizePricePayloadDetailed({
        ...row,
        priceListId: row.priceListId || body?.priceListId,
      });
      if ('error' in result) {
        errors.push(`Row ${i + 1}: ${result.error}`);
        return;
      }
      upsertSalesAdminPriceRule(ruleFromPayload(result.payload, newSalesAdminId('PR')), true);
      created += 1;
    });
    return ok(req, { created, updated: 0, errors });
  }
  const priceOne = /^price-lists\/([^/]+)$/i.exec(sub);
  if (priceOne && method === 'PUT') {
    const id = decodeURIComponent(priceOne[1]);
    const payload = normalizePricePayload(readJsonBody(req.body));
    if (!payload) {
      return ok(req, { error: 'invalid_price_rule' }, 400);
    }
    const existing = listSalesAdminPriceRules().find(r => r.id === id);
    if (!existing) {
      return ok(req, { error: 'not_found' }, 404);
    }
    return ok(
      req,
      upsertSalesAdminPriceRule(
        {
          ...ruleFromPayload(payload, id),
          priceListId: payload.priceListId || existing.priceListId,
          customerNo: existing.customerNo,
        },
        false
      )
    );
  }
  if (priceOne && method === 'DELETE') {
    const id = decodeURIComponent(priceOne[1]);
    return deleteSalesAdminPriceRule(id) ? ok(req, null, 204) : ok(req, { error: 'not_found' }, 404);
  }

  // catalogs
  if (sub === 'catalogs' && method === 'GET') {
    return ok(req, { elements: listSalesAdminCatalogs() });
  }
  if (sub === 'catalogs' && method === 'POST') {
    const payload = readJsonBody<SalesAdminCatalogPayload>(req.body);
    if (!payload?.name || !Array.isArray(payload.skus)) {
      return ok(req, { error: 'invalid_catalog' }, 400);
    }
    const created = upsertSalesAdminCatalog(
      {
        id: newSalesAdminId('CAT'),
        customerNo: '',
        name: payload.name,
        description: payload.description,
        skus: payload.skus.map(s => String(s).trim()).filter(Boolean),
        updatedAt: '',
        updatedBy: '',
      },
      true
    );
    return ok(req, created, 201);
  }
  if (sub === 'catalogs/upload' && method === 'POST') {
    const body = readJsonBody<{
      rows?: { name: string; description?: string; sku: string }[];
    }>(req.body);
    const byName = new Map<string, { name: string; description?: string; skus: string[] }>();
    const errors: string[] = [];
    (body?.rows || []).forEach((row, i) => {
      if (!row?.name || !row?.sku) {
        errors.push(`Row ${i + 1}: name and sku required`);
        return;
      }
      const key = row.name.trim().toLowerCase();
      const existing = byName.get(key) || {
        name: row.name.trim(),
        description: row.description,
        skus: [] as string[],
      };
      existing.skus.push(String(row.sku).trim());
      byName.set(key, existing);
    });
    let created = 0;
    let updated = 0;
    const existingCatalogs = listSalesAdminCatalogs();
    byName.forEach(cat => {
      const found = existingCatalogs.find(c => c.name.toLowerCase() === cat.name.toLowerCase());
      if (found) {
        upsertSalesAdminCatalog(
          {
            ...found,
            description: cat.description ?? found.description,
            skus: Array.from(new Set([...found.skus, ...cat.skus])),
          },
          false
        );
        updated += 1;
      } else {
        upsertSalesAdminCatalog(
          {
            id: newSalesAdminId('CAT'),
            customerNo: '',
            name: cat.name,
            description: cat.description,
            skus: Array.from(new Set(cat.skus)),
            updatedAt: '',
            updatedBy: '',
          },
          true
        );
        created += 1;
      }
    });
    return ok(req, { created, updated, errors });
  }
  const catOne = /^catalogs\/([^/]+)$/i.exec(sub);
  if (catOne && method === 'PUT') {
    const id = decodeURIComponent(catOne[1]);
    const payload = readJsonBody<SalesAdminCatalogPayload>(req.body);
    const existing = listSalesAdminCatalogs().find(c => c.id === id);
    if (!existing || !payload?.name) {
      return ok(req, { error: 'not_found' }, 404);
    }
    return ok(
      req,
      upsertSalesAdminCatalog(
        {
          ...existing,
          name: payload.name,
          description: payload.description,
          skus: (payload.skus || []).map(s => String(s).trim()).filter(Boolean),
        },
        false
      )
    );
  }
  if (catOne && method === 'DELETE') {
    const id = decodeURIComponent(catOne[1]);
    return deleteSalesAdminCatalog(id) ? ok(req, null, 204) : ok(req, { error: 'not_found' }, 404);
  }

  return null;
}
